/**
 * OptionEditor Drawer (Uber Eats Style)
 * Verwaltet Modifier Groups
 */

import React, { useEffect, useState } from "react";
import { Save, Trash2, RefreshCw, Plus, X, ChevronRight } from "lucide-react";
import { DishOption, DishOptionItem } from "@tasty/api";
import { DishOptionClient, DishOptionItemClient, removeTypename } from "../../api-client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { formatPrice } from "@/lib/utils";
import { v4 as uuidv4 } from "uuid";

interface OptionEditorDrawerProps {
  option: DishOption | null;
  restaurantId: string;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}

export function OptionEditorDrawer({
  option,
  restaurantId,
  onClose,
  onSaved,
  onDeleted,
}: OptionEditorDrawerProps) {
  const isEditMode = Boolean(option);
  const [title, setTitle] = useState("");
  const [obligatory, setObligatory] = useState(false);
  const [multipleSelection, setMultipleSelection] = useState(false);
  const [items, setItems] = useState<DishOptionItem[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTitle(option?.title || "");
    setObligatory(Boolean(option?.obligatory));
    setMultipleSelection(Boolean(option?.multipleSelection));
    if (option?.id) {
      DishOptionItemClient.queryList({ filter: { dishOptionID: { eq: option.id } } })
        .then(setItems);
    } else {
      setItems([]);
    }
  }, [option]);

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        obligatory,
        multipleSelection,
        restaurantID: restaurantId,
      };

      if (isEditMode && option) {
        await DishOptionClient.updateItem({ id: option.id, ...payload });
      } else {
        await DishOptionClient.createItem(payload);
      }
      toast.success("Anpassung gespeichert");
      onSaved();
      onClose();
    } catch (e) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SheetContent className="flex flex-col h-full sm:max-w-xl p-0 overflow-hidden border-l shadow-2xl">
      <SheetHeader className="p-6 border-b shrink-0 bg-muted/5">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <SheetTitle>{isEditMode ? "Anpassungsgruppe bearbeiten" : "Neue Anpassung"}</SheetTitle>
            <SheetDescription>Modifier Groups wie Extras oder Größen</SheetDescription>
          </div>
          <Button onClick={handleSave} disabled={saving || !title.trim()} size="sm">
            {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Speichern
          </Button>
        </div>
      </SheetHeader>

      <div className="flex-1 overflow-y-auto p-6 space-y-8 scrollbar-hide">
        <div className="space-y-4">
          <h3 className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/70 ml-1">Eigenschaften</h3>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-muted-foreground ml-1">Titel der Gruppe</label>
              <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="z.B. Käse wählen" className="h-11 rounded-xl" />
            </div>
            <div className="flex items-center justify-between p-4 rounded-xl border bg-muted/5">
              <div className="space-y-0.5">
                <p className="text-sm font-bold">Pflichtfeld</p>
                <p className="text-[10px] text-muted-foreground">Kunden müssen mindestens ein Item wählen</p>
              </div>
              <Switch checked={obligatory} onCheckedChange={setObligatory} />
            </div>
            <div className="flex items-center justify-between p-4 rounded-xl border bg-muted/5">
              <div className="space-y-0.5">
                <p className="text-sm font-bold">Mehrfachauswahl</p>
                <p className="text-[10px] text-muted-foreground">Kunden können mehrere Items wählen</p>
              </div>
              <Switch checked={multipleSelection} onCheckedChange={setMultipleSelection} />
            </div>
          </div>
        </div>

        {isEditMode && (
          <div className="space-y-4 pt-6 border-t border-dashed">
            <div className="flex items-center justify-between ml-1">
              <h3 className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/70">Einzelne Auswahlmöglichkeiten</h3>
              <Button variant="ghost" size="sm" className="h-7 text-[9px] font-black uppercase"><Plus className="w-3 h-3 mr-1" /> Item hinzufügen</Button>
            </div>
            
            <div className="grid grid-cols-1 gap-2">
              {items.map(item => (
                <div key={item.id} className="flex items-center justify-between p-3 border rounded-xl bg-card shadow-sm">
                  <span className="text-sm font-bold">{item.title}</span>
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-black tabular-nums">{formatPrice(item.price || 0)}</span>
                    <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg text-muted-foreground"><X className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </SheetContent>
  );
}

